import { useEffect, useMemo, useState } from 'react';
import './watch-game.styles.scss';
import { Link } from 'react-router-dom';
import { REPLAY_SPEEDS, halftimeIndex, liveBox, replayDelays } from '@shared/replay.js';
import { positions } from '@/utils/positions';
import { textColorOn } from '@shared/teamList.js';
import { listenToDoc } from '@/utils/getDataFromDb';
import TeamBadge from '@/components/SHARED/team-badge/team-badge.component';
import ThreeDotsLoader from '@/components/loader/three-dots-loader.component';
import MatchupPreview from '@/components/HOME/matchup-preview/matchup-preview.component';

const SPEED_KEY = 'rotations-replay-speed';

// Replays one of the manager's games from its play-by-play at the speed
// they choose, after a matchup preview (MatchupPreview) of the two teams, revealing nothing (score included) ahead of the play on
// screen. It pauses at halftime with the stats so far, and the stats or
// the matchup (the preview again) can be opened, pausing the
// game, at any time. onSeen(game) is called once
// the game has been watched or skipped to the end. next: the next unseen
// game, if any. teams, schedule, rankHistory: the league summaries, for
// the preview.
const WatchGame = ({ leagueId, gameId, league, teamId, next, onSeen, gameLink, watchLink, base, teams, schedule, rankHistory }) => {

    const [ game, setGame ] = useState(undefined);
    const [ shown, setShown ] = useState(0);
    // The matchup preview comes first; the clock starts on "Tip off".
    const [ started, setStarted ] = useState(false);
    const [ playing, setPlaying ] = useState(true);
    const [ speed, setSpeed ] = useState(() => {
        try { return localStorage.getItem(SPEED_KEY) ?? 'normal'; } catch { return 'normal'; }
    });
    const [ seenSent, setSeenSent ] = useState(false);
    const [ showStats, setShowStats ] = useState(false);
    const [ showTape, setShowTape ] = useState(false);
    const [ halftime, setHalftime ] = useState(false);
    const [ halfDone, setHalfDone ] = useState(false);

    useEffect(() => {
        setGame(undefined);
        setShown(0);
        setStarted(false);
        setPlaying(true);
        setSeenSent(false);
        setShowStats(false);
        setShowTape(false);
        setHalftime(false);
        setHalfDone(false);
        let unsub;
        listenToDoc({ 'path': `leagues/${leagueId}/games/${gameId}`, 'callback': setGame, 'handleListener': (u) => { unsub = u; } });
        return () => { if (unsub) unsub(); };
    }, [leagueId, gameId])

    const pbp = useMemo(() => game?.pbp ?? [], [game]);
    const minutes = league.settings.minutesPerGame;
    const seconds = REPLAY_SPEEDS.find(s => s.key === speed)?.seconds ?? 240;
    const delays = useMemo(() => replayDelays(pbp, minutes, seconds), [pbp, minutes, seconds]);
    const done = pbp.length > 0 && shown >= pbp.length - 1;
    const halfIdx = useMemo(() => halftimeIndex(pbp, minutes), [pbp, minutes]);

    // The next play, after its wait; at the end of the first half, stop
    // for halftime and show the stats so far.
    useEffect(() => {
        if (!started || !playing || !pbp.length || done) return;
        if (shown === halfIdx && !halfDone) {
            setHalfDone(true);
            setHalftime(true);
            setShowStats(true);
            setShowTape(false);
            setPlaying(false);
            return;
        }
        const t = setTimeout(() => setShown(s => s + 1), delays[shown + 1] ?? 500);
        return () => clearTimeout(t);
    }, [started, playing, shown, delays, pbp.length, done, halfIdx, halfDone])

    function resume() {
        setHalftime(false);
        setShowStats(false);
        setShowTape(false);
        setPlaying(true);
    }

    function toggleStats() {
        if (showStats) {
            resume();
        } else {
            setShowStats(true);
            setShowTape(false);
            setPlaying(false);
        }
    }

    // Closing it at halftime leaves the game waiting for the second half.
    function toggleTape() {
        if (showTape) {
            if (halftime) setShowTape(false);
            else resume();
        } else {
            setShowTape(true);
            setShowStats(false);
            setPlaying(false);
        }
    }

    // Watched to the end (or skipped there): no more asking about it.
    useEffect(() => {
        if (done && game && !seenSent) {
            setSeenSent(true);
            onSeen(game);
        }
    }, [done, game, seenSent, onSeen])

    function pickSpeed(key) {
        setSpeed(key);
        try { localStorage.setItem(SPEED_KEY, key); } catch { /* per-device convenience only */ }
    }

    if (game === undefined) return <div className='center-col'><ThreeDotsLoader /></div>;
    if (!game || !pbp.length) {
        return (
            <div className='g-card'>
                <p>This game&apos;s play-by-play isn&apos;t available to watch.</p>
                <Link to={gameLink(gameId)} className='g-button primary'>See the result</Link>
            </div>
        );
    }

    const periods = minutes >= 48 ? 4 : 2;
    const now = pbp[shown];
    const plays = pbp.slice(0, shown + 1).map((e, i) => ({ e, i, scored: i > 0 && (e.home !== pbp[i - 1].home || e.away !== pbp[i - 1].away) })).reverse();
    const mine = game.home.id === teamId ? 'home' : 'away';
    const won = done && game.winner === teamId;
    const sideName = (side) => (side === 'home' ? game.home.abbr : side === 'away' ? game.away.abbr : '');
    const chips = chipColors(game);
    const speedTabs = (
        <div className='speed-tabs' role='radiogroup' aria-label='Speed'>
            {REPLAY_SPEEDS.map(s => (
                <button key={s.key} role='radio' aria-checked={speed === s.key} className={`speed-tab ${speed === s.key ? 'active' : ''}`} onClick={() => pickSpeed(s.key)}>
                    {s.label}
                </button>
            ))}
        </div>
    );

    if (!started) {
        const skip = () => { setStarted(true); setShown(pbp.length - 1); };
        return (
            <div className='watch-game'>
                <MatchupPreview leagueId={leagueId} league={league} game={game} teams={teams} schedule={schedule} rankHistory={rankHistory} teamId={teamId} />
                <div className='g-card controls start-card'>
                    {speedTabs}
                    <div className='control-row'>
                        <button className='g-button primary' onClick={() => setStarted(true)}>Tip off</button>
                        <button className='g-button' onClick={skip}>Skip to the final</button>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className='watch-game'>
            <div className='g-card scoreboard'>
                <div className='sb-team'>
                    <TeamBadge team={game.away} size='md' />
                    <span className='sb-name'>{game.away.name}</span>
                    <span className='sb-score'>{now.away}</span>
                </div>
                <div className='sb-middle'>
                    <span className='sb-period'>{done ? 'Final' : periodLabel(now.period, periods)}</span>
                    {!done && <span className='sb-clock'>{now.clock}</span>}
                    <span className='meta'>{game.neutral ? 'Neutral site' : `at ${game.home.abbr}`}</span>
                </div>
                <div className='sb-team'>
                    <TeamBadge team={game.home} size='md' />
                    <span className='sb-name'>{game.home.name}</span>
                    <span className='sb-score'>{now.home}</span>
                </div>
            </div>

            {done ?
                <div className={`g-card final-card ${won ? 'won' : 'lost'}`} role='status'>
                    <h3>{won ? 'You won!' : 'Final'}: {game.away.abbr} {now.away}, {game.home.abbr} {now.home}{game.overtimes ? ` (${game.overtimes > 1 ? `${game.overtimes}OT` : 'OT'})` : ''}</h3>
                    <div className='final-actions'>
                        <Link to={gameLink(gameId)} className='g-button primary'>Box score</Link>
                        {next && <Link to={watchLink(next.id)} className='g-button'>Watch the next game</Link>}
                        <Link to={base} className='g-button'>My Team</Link>
                    </div>
                </div> :
                <div className='g-card controls'>
                    {halftime &&
                        <div className='halftime' role='status'>
                            <b>Halftime</b>
                            <button className='g-button primary' onClick={resume}>Start the second half</button>
                        </div>
                    }
                    {speedTabs}
                    <div className='control-row'>
                        <button className='g-button primary' onClick={() => (playing ? setPlaying(false) : resume())}>{playing ? 'Pause' : 'Play'}</button>
                        <button className={`g-button ${showStats ? 'active' : ''}`} aria-expanded={showStats} onClick={toggleStats}>{showStats ? 'Hide stats' : 'Game stats'}</button>
                        <button className={`g-button ${showTape ? 'active' : ''}`} aria-expanded={showTape} onClick={toggleTape}>{showTape ? 'Hide matchup' : 'View Matchup'}</button>
                        <button className='g-button' onClick={() => { setShowStats(false); setShowTape(false); setHalftime(false); setShown(pbp.length - 1); }}>Skip to end</button>
                    </div>
                </div>
            }

            {showStats && !done &&
                <LiveStats game={game} box={liveBox(pbp, shown)} label={halftime ? 'Halftime stats' : `Stats through ${now.clock}, ${periodLabel(now.period, periods).toLowerCase()}`} />
            }

            {showTape && !done &&
                <MatchupPreview leagueId={leagueId} league={league} game={game} teams={teams} schedule={schedule} rankHistory={rankHistory} teamId={teamId} compact />
            }

            <div className='g-card feed-card'>
                <ol className='play-feed' aria-live='polite'>
                    {plays.map(({ e, i, scored }) => (
                        <li key={i} className={`play ${e.side ?? 'neutral'} ${scored ? 'scored' : ''} ${e.side && (e.side === mine) ? 'mine' : ''}`}>
                            <span className='play-clock'>{e.clock}</span>
                            <span className='play-team'>{e.side && <span className='team-chip' style={chips[e.side]}>{sideName(e.side)}</span>}</span>
                            <span className='play-text'>{e.text}</span>
                            <span className='play-score'>{e.away}-{e.home}</span>
                        </li>
                    ))}
                </ol>
            </div>
        </div>
    )
}

// The box score as of the play on screen: both teams, everyone who
// dressed (starters first), plus team totals.
const LiveStats = ({ game, box, label }) => {
    if (!box) {
        return <div className='g-card'><p className='meta'>Live stats aren&apos;t available for this game (it was played before the replay kept them). The full box score is on the result page.</p></div>;
    }
    const line = (b) => b ?? {};
    const pct = (m, a) => `${m ?? 0}-${a ?? 0}`;
    return (
        <div className='g-card live-stats'>
            <h3>{label}</h3>
            {['away', 'home'].map(side => {
                const team = game[side];
                const ids = Object.keys(team.roster ?? {}).sort((a, b) =>
                    (team.players?.[b]?.starter ? 1 : 0) - (team.players?.[a]?.starter ? 1 : 0) ||
                    (line(box[b]).pts ?? 0) - (line(box[a]).pts ?? 0));
                const total = {};
                for (const id of ids) {
                    for (const [k, n] of Object.entries(line(box[id]))) total[k] = (total[k] ?? 0) + n;
                }
                const row = (b) => [
                    b.pts ?? 0, (b.oreb ?? 0) + (b.dreb ?? 0), b.ast ?? 0, pct(b.fgm, b.fga), pct(b.tpm, b.tpa), pct(b.ftm, b.fta),
                    b.stl ?? 0, b.blk ?? 0, b.tov ?? 0, b.pf ?? 0,
                ];
                return (
                    <div key={side} className='live-team'>
                        <h4><TeamBadge team={team} size='sm' /> {team.name}</h4>
                        <div className='table-scroll'>
                            <table className='g-table live-table'>
                                <thead>
                                    <tr><th>Player</th><th>PTS</th><th>REB</th><th>AST</th><th>FG</th><th>3PT</th><th>FT</th><th>STL</th><th>BLK</th><th>TO</th><th>PF</th></tr>
                                </thead>
                                <tbody>
                                    {ids.map(id => {
                                        const p = team.roster[id];
                                        return (
                                            <tr key={id} className={team.players?.[id]?.starter ? 'starter' : ''}>
                                                <td className='live-name'>{p.fName[0]}. {p.lName} <span className='meta'>{positions[p.pos1 - 1]?.short}</span></td>
                                                {row(line(box[id])).map((v, i) => <td key={i} className='num'>{v}</td>)}
                                            </tr>
                                        );
                                    })}
                                    <tr className='totals'>
                                        <td>Totals</td>
                                        {row(total).map((v, i) => <td key={i} className='num'>{v}</td>)}
                                    </tr>
                                </tbody>
                            </table>
                        </div>
                    </div>
                );
            })}
            <p className='meta'>Starters in bold. Minutes come with the final box score.</p>
        </div>
    );
}

// Colors for each team's abbreviation in the play feed: the team's
// primary color, except that the away team switches to its secondary
// color when the two primaries look alike.
function chipColors(game) {
    const home = game.home.color1 ?? '#666666';
    let away = game.away.color1 ?? '#999999';
    const alt = game.away.color2;
    if (colorDistance(home, away) < 120 && alt && colorDistance(home, alt) > colorDistance(home, away)) away = alt;
    const style = (bg) => ({ backgroundColor: bg, color: textColorOn(bg) });
    return { home: style(home), away: style(away) };
}

function colorDistance(a, b) {
    const rgb = (hex) => [1, 3, 5].map(i => parseInt(hex.slice(i, i + 2), 16) || 0);
    const [x, y] = [rgb(a), rgb(b)];
    return Math.hypot(x[0] - y[0], x[1] - y[1], x[2] - y[2]);
}

function periodLabel(n, periods) {
    if (n > periods) {
        const ot = n - periods;
        return ot === 1 ? 'Overtime' : `Overtime ${ot}`;
    }
    if (periods === 2) return n === 1 ? '1st Half' : '2nd Half';
    return `${['1st', '2nd', '3rd', '4th'][n - 1]} Quarter`;
}

export default WatchGame
